import { useCallback, useEffect, useState } from "react";
import type { TextInputKeyPressEvent } from "react-native";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseListKeyboardOptions {
  /** Total number of items in the list. */
  readonly totalItems: number;
  /** Whether the list is currently visible. */
  readonly isOpen: boolean;
  /** Open the list (triggered by arrow keys when closed). */
  readonly open: () => void;
  /** Close the list (triggered by Escape). */
  readonly close: () => void;
  /** Called when Enter is pressed with a highlighted index (>= 0). */
  readonly onHighlightSelect: (index: number) => void;
  /** Called when Enter is pressed with no highlight. */
  readonly onSubmit?: ((inputValue: string) => void) | undefined;
  /** Current input value (passed to onSubmit). */
  readonly inputValue?: string | undefined;
  /** If true, navigation does not wrap from last to first. */
  readonly disableListWrap?: boolean | undefined;
}

export interface UseListKeyboardReturn {
  /** Index of the currently highlighted item (-1 = none). */
  readonly highlightedIndex: number;
  /** Manually set the highlighted index. */
  readonly setHighlightedIndex: (index: number) => void;
  /** Reset highlight to -1. */
  readonly resetHighlight: () => void;
  /** Key press handler to attach to the TextInput. */
  readonly handleKeyPress: (e: TextInputKeyPressEvent) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useListKeyboard(config: UseListKeyboardOptions): UseListKeyboardReturn {
  const {
    totalItems,
    isOpen,
    open,
    close,
    onHighlightSelect,
    onSubmit,
    inputValue = "",
    disableListWrap = false,
  } = config;

  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Clamp highlight when list shrinks below the current index
  useEffect(() => {
    if (highlightedIndex >= totalItems) {
      setHighlightedIndex(totalItems > 0 ? totalItems - 1 : -1);
    }
  }, [totalItems, highlightedIndex]);

  const resetHighlight = useCallback(() => {
    setHighlightedIndex(-1);
  }, []);

  const handleKeyPress = useCallback(
    (e: TextInputKeyPressEvent) => {
      const { key } = e.nativeEvent;

      switch (key) {
        case "ArrowDown": {
          e.preventDefault();
          if (!isOpen) {
            open();
            return;
          }
          setHighlightedIndex((prev) => {
            if (prev < totalItems - 1) return prev + 1;
            return disableListWrap ? prev : 0;
          });
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          if (!isOpen) {
            open();
            return;
          }
          setHighlightedIndex((prev) => {
            if (prev > 0) return prev - 1;
            return disableListWrap ? prev : totalItems - 1;
          });
          break;
        }
        case "Home": {
          if (isOpen && totalItems > 0) {
            e.preventDefault();
            setHighlightedIndex(0);
          }
          break;
        }
        case "End": {
          if (isOpen && totalItems > 0) {
            e.preventDefault();
            setHighlightedIndex(totalItems - 1);
          }
          break;
        }
        case "Enter": {
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < totalItems) {
            onHighlightSelect(highlightedIndex);
          } else {
            onSubmit?.(inputValue);
          }
          break;
        }
        case "Escape": {
          // Guarded on isOpen, like Home/End above: a closed combobox has
          // nothing to dismiss, so consuming the key there would strand it.
          // Enclosing surfaces treat a defaulted Escape as already handled
          // (see DialogModal on web), which meant that once the input had been
          // focused, every later Escape was swallowed and the dialog could only
          // be closed via its button or backdrop.
          if (isOpen) {
            e.preventDefault();
            close();
          }
          break;
        }
      }
    },
    [totalItems, highlightedIndex, isOpen, open, close, onHighlightSelect, onSubmit, inputValue, disableListWrap],
  );

  return { highlightedIndex, setHighlightedIndex, resetHighlight, handleKeyPress };
}
